import { useState, useEffect, useCallback } from "react";
import { Globe, Check, X, Clock, RefreshCw, CheckCircle2, ArrowRight, Copy, Image, ChevronDown, Cloud, Wifi, Shield } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const STATUS_OPTS = [
  { value: "in_progress", label: "In Progress", color: "bg-blue-500 hover:bg-blue-600 text-white" },
  { value: "awaiting_dns", label: "Awaiting DNS", color: "bg-yellow-500 hover:bg-yellow-600 text-black" },
  { value: "approved", label: "Approve", color: "bg-green-500 hover:bg-green-600 text-white" },
  { value: "active", label: "Mark Active", color: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { value: "rejected", label: "Reject", color: "bg-red-500 hover:bg-red-600 text-white" },
];

const STATUS_BADGE = {
  pending: { bg: "bg-yellow-500/15", text: "text-yellow-400", label: "Pending Review" },
  in_progress: { bg: "bg-blue-500/15", text: "text-blue-400", label: "In Progress" },
  awaiting_dns: { bg: "bg-orange-500/15", text: "text-orange-400", label: "Awaiting DNS" },
  approved: { bg: "bg-green-500/15", text: "text-green-400", label: "Approved" },
  active: { bg: "bg-emerald-500/15", text: "text-emerald-400", label: "Active" },
  rejected: { bg: "bg-red-500/15", text: "text-red-400", label: "Rejected" },
};

const AdminDomainsTab = ({ authToken, onMessage }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [dnsInstructions, setDnsInstructions] = useState("");
  const [estHours, setEstHours] = useState("");
  const [templates, setTemplates] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [healthResults, setHealthResults] = useState({});
  const [healthLoading, setHealthLoading] = useState({});
  const [cfStatus, setCfStatus] = useState(null);
  const [dnsVerifyResults, setDnsVerifyResults] = useState({});
  const [dnsVerifyLoading, setDnsVerifyLoading] = useState({});

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/admin/requests`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/domains/dns-templates`)
      .then(r => r.json())
      .then(d => setTemplates(d.templates || {}))
      .catch(() => {});
    // Fetch Cloudflare status
    fetch(`${BACKEND_URL}/api/domains/admin/cloudflare-status`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => r.json())
      .then(d => setCfStatus(d))
      .catch(() => {});
  }, [authToken]);

  const checkHealth = async (domain) => {
    setHealthLoading(prev => ({ ...prev, [domain]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/health/${encodeURIComponent(domain)}`);
      if (res.ok) {
        const data = await res.json();
        setHealthResults(prev => ({ ...prev, [domain]: data }));
      }
    } catch (e) {}
    finally { setHealthLoading(prev => ({ ...prev, [domain]: false })); }
  };

  const verifyDns = async (domain) => {
    setDnsVerifyLoading(prev => ({ ...prev, [domain]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/admin/verify-dns/${encodeURIComponent(domain)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDnsVerifyResults(prev => ({ ...prev, [domain]: data }));
      }
    } catch (e) {}
    finally { setDnsVerifyLoading(prev => ({ ...prev, [domain]: false })); }
  };

  const handleAction = async (userId, status) => {
    try {
      const body = { status, admin_notes: adminNotes, dns_instructions: dnsInstructions };
      if (estHours) body.estimated_hours = parseInt(estHours);
      const res = await fetch(`${BACKEND_URL}/api/domains/admin/requests/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        const cfInfo = data.cloudflare;
        if (status === "approved" && cfInfo?.success) {
          onMessage?.(`Domain approved & Custom Hostname provisioned in Cloudflare! SSL: ${cfInfo.ssl_status || "pending"}`);
        } else if (status === "approved" && cfInfo && !cfInfo.success) {
          onMessage?.(`Domain approved but Cloudflare provisioning failed: ${cfInfo.error}. You can retry manually.`);
        } else {
          onMessage?.(`Domain request updated to: ${status}`);
        }
        setActionId(null);
        setAdminNotes("");
        setDnsInstructions("");
        setEstHours("");
        fetchRequests();
      }
    } catch (e) { onMessage?.("Action failed"); }
  };

  const refreshHostnameStatus = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/admin/refresh-hostname-status/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        onMessage?.("Cloudflare status refreshed");
        fetchRequests();
      }
    } catch (e) {}
  };

  const applyTemplate = (templateId) => {
    const t = templates[templateId];
    if (t) setDnsInstructions(t.instructions);
    setShowTemplates(false);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><RefreshCw className="w-6 h-6 text-white/20 animate-spin" /></div>;
  }

  const pendingCount = requests.filter(r => ["pending", "in_progress", "awaiting_dns"].includes(r.status)).length;
  const activeCount = requests.filter(r => ["approved", "active"].includes(r.status)).length;

  return (
    <div className="space-y-4" data-testid="admin-domains-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-400" />
            Domain Requests
          </h3>
          <p className="text-white/30 text-xs mt-1">
            {pendingCount} pending &middot; {activeCount} active &middot; {requests.length} total
          </p>
        </div>
        <button onClick={fetchRequests} className="p-2 text-white/40 hover:text-white rounded-lg hover:bg-white/5 transition">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Cloudflare Automation Status */}
      {cfStatus && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${cfStatus.configured ? "bg-emerald-500/5 border-emerald-500/15" : "bg-red-500/5 border-red-500/15"}`} data-testid="cloudflare-status-banner">
          <Cloud className={`w-5 h-5 ${cfStatus.configured ? "text-emerald-400" : "text-red-400"}`} />
          <div className="flex-1 min-w-0">
            <p className="text-white text-xs font-semibold flex items-center gap-2">
              Cloudflare DNS Automation
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${cfStatus.configured ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
                {cfStatus.configured ? "CONNECTED" : "NOT CONFIGURED"}
              </span>
              {cfStatus.saas_enabled && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/15 text-orange-400">
                  SaaS ENABLED
                </span>
              )}
            </p>
            {cfStatus.configured && (
              <p className="text-white/30 text-[10px] mt-0.5">
                Zone: {cfStatus.zone_name} &middot; Plan: {cfStatus.plan}
                {cfStatus.fallback_origin && <> &middot; Fallback: {cfStatus.fallback_origin} ({cfStatus.fallback_status})</>}
                {cfStatus.custom_hostname_count !== undefined && <> &middot; Custom Hostnames: {cfStatus.custom_hostname_count}/100</>}
              </p>
            )}
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-2xl">
          <Globe className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No domain requests yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req, i) => {
            const badge = STATUS_BADGE[req.status] || STATUS_BADGE.pending;
            const isExpanded = actionId === req.user_id;
            const health = healthResults[req.domain];
            const isManageable = ["pending", "in_progress", "awaiting_dns", "approved"].includes(req.status);

            return (
              <div key={i} className="bg-white/[0.03] border border-white/8 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-semibold text-sm">{req.domain}</p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => checkHealth(req.domain)} className="text-white/20 hover:text-white/50 transition" title="Check DNS health">
                        {healthLoading[req.domain] ? <RefreshCw className="w-3 h-3 animate-spin" /> : (
                          health?.dns_resolves ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400" /> : <Globe className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <button onClick={() => verifyDns(req.domain)} className="text-white/20 hover:text-white/50 transition" title="Verify DNS records via Cloudflare">
                        {dnsVerifyLoading[req.domain] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                    </div>
                    <p className="text-white/40 text-xs mt-0.5">
                      {req.station_name || "Unknown"} &middot; {req.email}
                    </p>
                    {req.notes && <p className="text-white/25 text-xs mt-1 italic">"{req.notes}"</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                    {isManageable && (
                      <button
                        onClick={() => { setActionId(isExpanded ? null : req.user_id); setAdminNotes(req.admin_notes || ""); setDnsInstructions(req.dns_instructions || ""); setEstHours(req.estimated_hours || ""); }}
                        className="text-white/40 hover:text-white text-xs px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition"
                      >
                        {isExpanded ? "Cancel" : "Manage"}
                      </button>
                    )}
                  </div>
                </div>

                {/* DNS Screenshots from user */}
                {req.dns_screenshots?.length > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <Image className="w-3.5 h-3.5 text-white/30" />
                    <span className="text-white/30 text-xs">DNS proof uploaded ({req.dns_screenshots.length}):</span>
                    {req.dns_screenshots.map((s, j) => (
                      <a key={j} href={s.url} target="_blank" rel="noopener noreferrer"
                        className="text-purple-400 text-xs hover:underline">{s.filename || `Screenshot ${j+1}`}</a>
                    ))}
                  </div>
                )}

                {/* Health check result inline */}
                {health && (
                  <div className="mt-2 flex gap-3 text-[10px]">
                    <span className={health.dns_resolves ? "text-green-400" : "text-red-400"}>DNS: {health.dns_resolves ? `OK (${health.ip_address})` : "FAIL"}</span>
                    <span className={health.http_reachable ? "text-green-400" : "text-red-400"}>HTTP: {health.http_reachable ? "OK" : "FAIL"}</span>
                    <span className={health.ssl_valid ? "text-green-400" : "text-red-400"}>SSL: {health.ssl_valid ? "OK" : "FAIL"}</span>
                    <span className={health.points_to_platform ? "text-green-400" : "text-yellow-400"}>Platform: {health.points_to_platform ? "OK" : "Not detected"}</span>
                  </div>
                )}

                {/* Deep DNS verification result */}
                {dnsVerifyResults[req.domain] && (
                  <div className="mt-2 flex items-center gap-3 text-[10px] px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
                    <Shield className="w-3 h-3 text-purple-400 flex-shrink-0" />
                    <span className={dnsVerifyResults[req.domain].dns_resolves ? "text-green-400" : "text-red-400"}>
                      DNS: {dnsVerifyResults[req.domain].dns_resolves ? "Resolves" : "Not resolving"}
                    </span>
                    {dnsVerifyResults[req.domain].cname_target && (
                      <span className="text-white/40">CNAME: {dnsVerifyResults[req.domain].cname_target}</span>
                    )}
                    {dnsVerifyResults[req.domain].ip_addresses?.length > 0 && (
                      <span className="text-white/40">IPs: {dnsVerifyResults[req.domain].ip_addresses.join(", ")}</span>
                    )}
                    <span className={dnsVerifyResults[req.domain].points_to_platform ? "text-green-400 font-bold" : "text-yellow-400"}>
                      {dnsVerifyResults[req.domain].points_to_platform ? "Points to platform" : "Not pointing to us"}
                    </span>
                  </div>
                )}

                {/* Cloudflare Custom Hostname Status */}
                {req.cf_hostname_id && (
                  <div className="mt-2 flex items-center gap-3 px-3 py-2 rounded-lg bg-orange-500/[0.03] border border-orange-500/10">
                    <Cloud className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                    <div className="flex items-center gap-3 text-[10px] flex-1">
                      <span className="text-white/50">CF Hostname:</span>
                      <span className={
                        req.cf_status === "active" ? "text-green-400 font-bold" :
                        req.cf_status === "pending" ? "text-yellow-400" : "text-white/40"
                      }>
                        {req.cf_status || "unknown"}
                      </span>
                      <span className="text-white/30">|</span>
                      <span className="text-white/50">SSL:</span>
                      <span className={
                        req.cf_ssl_status === "active" ? "text-green-400 font-bold" :
                        req.cf_ssl_status === "pending_validation" ? "text-yellow-400" :
                        req.cf_ssl_status === "pending_issuance" ? "text-blue-400" : "text-white/40"
                      }>
                        {req.cf_ssl_status || "unknown"}
                      </span>
                    </div>
                    <button
                      onClick={() => refreshHostnameStatus(req.user_id)}
                      className="text-orange-400/60 hover:text-orange-400 transition"
                      title="Refresh Cloudflare status"
                    >
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Expanded manage panel */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                    <input
                      value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                      placeholder="Admin notes (visible to user)"
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                    />

                    <div className="flex items-center gap-2">
                      <input
                        value={estHours} onChange={e => setEstHours(e.target.value.replace(/\D/g, ""))}
                        placeholder="Est. hours"
                        className="w-28 bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40"
                      />
                      <span className="text-white/20 text-xs">estimated setup time (hours)</span>
                    </div>

                    {/* DNS Templates */}
                    <div className="relative">
                      <button onClick={() => setShowTemplates(!showTemplates)}
                        className="flex items-center gap-2 text-purple-400 text-xs px-3 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/15 transition">
                        <ChevronDown className="w-3 h-3" /> DNS Templates
                      </button>
                      {showTemplates && (
                        <div className="absolute left-0 top-full mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-10 py-1 min-w-[200px]">
                          {Object.entries(templates).map(([k, v]) => (
                            <button key={k} onClick={() => applyTemplate(k)}
                              className="w-full text-left px-4 py-2 text-white/70 text-xs hover:bg-white/5 transition">
                              {v.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <textarea
                      value={dnsInstructions} onChange={e => setDnsInstructions(e.target.value)}
                      placeholder="DNS instructions for the user..."
                      rows={6}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40 resize-none font-mono"
                    />

                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => handleAction(req.user_id, opt.value)}
                          className={`${opt.color} px-4 py-2 rounded-xl text-xs font-bold transition`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-white/15 text-[10px] mt-2">
                  Submitted: {req.submitted_at ? new Date(req.submitted_at).toLocaleDateString() : "N/A"}
                  {req.reviewed_at && ` · Reviewed: ${new Date(req.reviewed_at).toLocaleDateString()}`}
                  {req.estimated_hours && ` · Est: ${req.estimated_hours}h`}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDomainsTab;
